// src/components.js — small, dependency-free Preact components rendered with the
// bound `html` template tag. Inline styles only; base `.aq-*` classes are injected
// by the kit. Each component is also attached to the AqViz global by src/kit.js so
// view authors can write `const { html, Card, KPI } = window.AqViz;` and use bare
// names like `html\`<\${Card}>...\``.

export function createComponents(html) {
  // Map a caller-supplied tone hint to a semantic modifier suffix ('ok'|'warn'|'err'|''=neutral). Reuses the
  // Badge/Callout vocabulary so tints stay consistent kit-wide. The caller passes tone EXPLICITLY — the kit
  // never infers it from a value (it knows no business thresholds). An unknown/absent tone → '' (no class),
  // so every gadget below is byte-identical to its old output when `tone` is omitted.
  const toneSuffix = (tone) => {
    const t = String(tone || '').toLowerCase();
    if (['ok', 'success', 'approved', 'complete', 'completed', 'good', 'pass', 'passed'].includes(t)) return 'ok';
    if (['warn', 'warning', 'pending', 'review', 'inprogress', 'in_progress', 'caution', 'open'].includes(t)) return 'warn';
    if (['err', 'error', 'rejected', 'failed', 'fail', 'bad', 'critical', 'overdue'].includes(t)) return 'err';
    return '';
  };

  // ---- primitives ---------------------------------------------------------
  const Badge = ({ text, tone }) => {
    const t = String(tone || '').toLowerCase();
    const cls = ['ok', 'success', 'approved', 'complete', 'completed'].includes(t)
      ? 'aq-badge aq-badge-ok'
      : ['warn', 'pending', 'review', 'inprogress', 'in_progress'].includes(t)
        ? 'aq-badge aq-badge-warn'
        : ['error', 'rejected', 'failed', 'fail'].includes(t)
          ? 'aq-badge aq-badge-err'
          : 'aq-badge';
    return html`<span class=${cls}>${text == null ? '' : String(text)}</span>`;
  };

  const Stat = ({ label, value, tone }) => {
    const s = toneSuffix(tone);
    const cls = s ? `aq-stat aq-stat--${s}` : 'aq-stat';
    return html`<div class=${cls}><div class="aq-stat-v">${value == null ? '—' : value}</div><div class="aq-stat-l">${label || ''}</div></div>`;
  };

  const KPI = ({ label, value, unit, tone }) => {
    const display =
      value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value))
        ? '—'
        : value;
    const s = toneSuffix(tone);
    const cls = s ? `aq-kpi aq-kpi--${s}` : 'aq-kpi';
    return html`<div class=${cls}>
      <div class="aq-kpi-v">${display}${unit ? html`<span class="aq-kpi-u"> ${unit}</span>` : null}</div>
      <div class="aq-kpi-l">${label || ''}</div>
    </div>`;
  };

  const Card = ({ title, children }) =>
    html`<section class="aq-card">
      ${title ? html`<h3 class="aq-card-t">${title}</h3>` : null}
      <div class="aq-card-b">${children}</div>
    </section>`;

  const Section = ({ title, children }) =>
    html`<section class="aq-section">
      ${title ? html`<h2 class="aq-section-t">${title}</h2>` : null}
      <div class="aq-section-b">${children}</div>
    </section>`;

  // ---- hero ---------------------------------------------------------------
  const Hero = ({ title, subtitle, meta }) => {
    const m = meta || {};
    const submitted = m.submittedAt || m.createdAt;
    const when = submitted ? formatDate(submitted) : '';
    const place = m.locationName || coords(m.latitude, m.longitude);
    return html`<header class="aq-hero">
      <div class="aq-hero-main">
        <h1 class="aq-hero-t">${title || 'Submission'}</h1>
        ${subtitle ? html`<p class="aq-hero-sub">${subtitle}</p>` : null}
        <div class="aq-hero-meta">
          ${m.submitterName ? html`<span>👤 ${m.submitterName}</span>` : null}
          ${when ? html`<span>🕓 ${when}</span>` : null}
          ${place ? html`<span>📍 ${place}</span>` : null}
        </div>
      </div>
      ${m.status ? html`<div class="aq-hero-status">${html`<${Badge} text=${m.status} tone=${m.status} />`}</div>` : null}
    </header>`;
  };

  // ---- field list (scalars only) -----------------------------------------
  const SKIP_TYPES = [
    'image', 'drawing', 'voice', 'video', 'document', 'file', 'signature',
    'repeatedform', 'repeated', 'attachment'
  ];
  const isScalarField = (f) => {
    if (!f) return false;
    const t = String(f.fieldType || '').toLowerCase();
    if (SKIP_TYPES.includes(t)) return false;
    const v = f.value;
    if (Array.isArray(v) && v.length && typeof v[0] === 'object') return false; // attachments/repeated
    return true;
  };

  const FieldList = ({ fields, d }) => {
    const rows = (Array.isArray(fields) ? fields : []).filter(isScalarField);
    if (!rows.length) return null;
    return html`<dl class="aq-dl">
      ${rows.map(
        (f) => html`<div class="aq-dl-row" key=${f.fieldKey}>
          <dt class="aq-dl-t">${d ? d.label(f.fieldKey) : f.fieldKey}</dt>
          <dd class="aq-dl-d">${scalarText(f.value)}</dd>
        </div>`
      )}
    </dl>`;
  };

  // ---- gallery ------------------------------------------------------------
  const Gallery = ({ items }) => {
    const list = (Array.isArray(items) ? items : []).filter((x) => x && x.url);
    if (!list.length) return null;
    return html`<div class="aq-gallery">
      ${list.map((it, i) => html`<figure class="aq-ga-item" key=${it.url + i}>${renderAttachment(it)}
        ${it.fileName ? html`<figcaption class="aq-ga-cap">${it.fileName}</figcaption>` : null}
        ${it.transcript ? html`<figcaption class="aq-ga-tr">“${it.transcript}”</figcaption>` : null}
      </figure>`)}
    </div>`;
  };

  function renderAttachment(it) {
    const type = String(it.type || '').toLowerCase();
    const ct = String(it.contentType || '').toLowerCase();
    if (['image', 'drawing', 'signature'].includes(type) || ct.startsWith('image/')) {
      return html`<a href=${it.url} target="_blank" rel="noopener noreferrer">
        <img class="aq-ga-img" src=${it.url} alt=${it.fileName || type} loading="lazy" />
      </a>`;
    }
    if (type === 'voice' || type === 'audio' || ct.startsWith('audio/')) {
      return html`<audio class="aq-ga-audio" controls src=${it.url}></audio>`;
    }
    if (type === 'video' || ct.startsWith('video/')) {
      return html`<video class="aq-ga-video" controls src=${it.url}></video>`;
    }
    return html`<a class="aq-ga-file" href=${it.url} target="_blank" rel="noopener noreferrer" download>
      📎 ${it.fileName || 'Download file'}
    </a>`;
  }

  // ---- lab results table --------------------------------------------------
  const LabTable = ({ rows }) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return null;
    return html`<div class="aq-tablewrap"><table class="aq-table">
      <thead><tr>
        <th>Analyte</th><th>Result</th><th>Detection limit</th><th>Confidence</th>
      </tr></thead>
      <tbody>
        ${list.map((r, i) => {
          const val = r.numberValue ?? r.stringValue ?? '—';
          const unit = r.unit ? ` ${r.unit}` : '';
          const conf =
            r.matchConfidence == null ? '' : `${Math.round(Number(r.matchConfidence) * 100)}%`;
          return html`<tr key=${i}>
            <td>${r.analyteName || r.fieldKey || '—'}</td>
            <td>${val === '—' ? '—' : `${val}${unit}`}</td>
            <td>${r.detectionLimit ?? '—'}</td>
            <td>${conf}</td>
          </tr>`;
        })}
      </tbody>
    </table></div>`;
  };

  // ---- repeated entries table --------------------------------------------
  const RepeatedTable = ({ entries, d, columns }) => {
    const list = Array.isArray(entries) ? entries : [];
    if (!list.length) return null;

    let cols = Array.isArray(columns) && columns.length ? columns : inferColumns(list);
    if (!cols.length) return null;

    // Columns may be plain string keys OR {key/fieldKey, label/title} objects. Normalize both so an
    // object column can never coerce to the literal "[object Object]" in a header or cell (TASK-0236).
    const colKey = (c) =>
      typeof c === 'string'
        ? c
        : c && typeof c === 'object'
          ? String(c.key || c.fieldKey || c.label || c.title || '')
          : String(c == null ? '' : c);
    const colLabel = (c) => {
      if (c && typeof c === 'object' && (c.label || c.title)) return String(c.label || c.title);
      const k = colKey(c);
      return d ? d.label(k) : k;
    };

    return html`<div class="aq-tablewrap"><table class="aq-table">
      <thead><tr><th>#</th>${cols.map((c) => html`<th key=${colKey(c)}>${colLabel(c)}</th>`)}</tr></thead>
      <tbody>
        ${list.map((e, i) => {
          const idx = (e.instanceIndex ?? i) + 1;
          const get = (k) => {
            if (e.h) return e.h.text(k);
            const f = (e.fields || []).find((x) => x && x.fieldKey === k);
            return f ? scalarText(f.value) : '';
          };
          return html`<tr key=${i}><td>${idx}</td>${cols.map((c) => html`<td key=${colKey(c)}>${get(colKey(c))}</td>`)}</tr>`;
        })}
      </tbody>
    </table></div>`;
  };

  function inferColumns(entries) {
    const seen = [];
    const set = new Set();
    for (const e of entries) {
      for (const f of e.fields || []) {
        if (!f || typeof f.fieldKey !== 'string') continue;
        if (!isScalarField(f)) continue;
        if (!set.has(f.fieldKey)) {
          set.add(f.fieldKey);
          seen.push(f.fieldKey);
        }
      }
    }
    return seen;
  }

  // ---- zero-dependency inline SVG chart ----------------------------------
  // Backward compatible with points:number[] (single series → identical render: line+area+dots, or kind:'bar').
  // Multi-series via points:[{name,points,color}] / points:number[][] OR an explicit series:[{name,points}] —
  // each series is a line+dots (area fill only for a lone series, to avoid muddy overlaps) plus a legend.
  // Optional xAxisLabel / yAxisLabel annotate the axes. kind:'bar' is single-series only (legacy).
  const TrendChart = ({ points, labels, title, kind, series, xAxisLabel, yAxisLabel }) => {
    // Resolve input into a list of normalized series: [{ name, color, nums }].
    let rawSeries = null;
    if (Array.isArray(series) && series.length) {
      rawSeries = series;
    } else if (
      Array.isArray(points) && points.length &&
      (Array.isArray(points[0]) || (points[0] && typeof points[0] === 'object' && 'points' in points[0]))
    ) {
      rawSeries = points;
    }
    const seriesList = (rawSeries
      ? rawSeries.map((s, i) => ({
          name: s && s.name != null ? String(s.name) : `Series ${i + 1}`,
          color: (s && s.color) || TREND_PALETTE[i % TREND_PALETTE.length],
          nums: normalizePoints(Array.isArray(s) ? s : s && s.points)
        }))
      : [{ name: '', color: TREND_PALETTE[0], nums: normalizePoints(points) }]
    ).filter((s) => s.nums.length > 0);
    if (!seriesList.length) return null;

    const showLegend = seriesList.length > 1;
    const isBar = !showLegend && String(kind || 'line').toLowerCase() === 'bar';

    const W = 600;
    const H = 240;
    const padL = yAxisLabel ? 54 : 44;
    const padR = 16;
    const padT = title ? 34 : 16;
    const padB = xAxisLabel ? 46 : 30;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    let min = Infinity;
    let max = -Infinity;
    for (const s of seriesList) {
      for (const v of s.nums) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    if (min === max) {
      // flat series: pad the range so it renders mid-height
      const delta = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
      min -= delta;
      max += delta;
    }
    const span = max - min || 1;

    const n = Math.max(...seriesList.map((s) => s.nums.length));
    const xFor = (i) => (n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW);
    const yFor = (v) => padT + plotH - ((v - min) / span) * plotH;

    // gridlines (4 horizontal bands)
    const grid = [0, 0.25, 0.5, 0.75, 1].map((t) => {
      const y = padT + plotH - t * plotH;
      const val = min + t * span;
      return { y, val };
    });

    // One line series (polyline + dots, optional area) sharing the plot's xFor/yFor.
    const drawLine = (nums, color, withArea) => {
      const pts = nums.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(' ');
      const path = nums.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
      const last = nums.length - 1;
      return html`<g>
        ${withArea
          ? html`<path d=${`${path} L ${xFor(last).toFixed(1)} ${(padT + plotH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(padT + plotH).toFixed(1)} Z`}
              fill=${color} opacity="0.08"></path>`
          : null}
        <polyline points=${pts} fill="none" stroke=${color} stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>
        ${nums.map((v, i) => html`<circle key=${i} cx=${xFor(i)} cy=${yFor(v)} r="3" fill=${color}></circle>`)}
      </g>`;
    };

    let shapeEls;
    if (isBar) {
      const nums = seriesList[0].nums;
      const color = seriesList[0].color;
      const bw = (plotW / n) * 0.62;
      shapeEls = nums.map((v, i) => {
        const cx = n === 1 ? padL + plotW / 2 : padL + (i + 0.5) * (plotW / n);
        const y = yFor(v);
        const barH = padT + plotH - y;
        return html`<rect key=${i} x=${cx - bw / 2} y=${y} width=${bw} height=${Math.max(0, barH)}
          rx="2" fill=${color} opacity="0.85"></rect>`;
      });
    } else {
      shapeEls = seriesList.map((s, si) => html`<g key=${si}>${drawLine(s.nums, s.color, seriesList.length === 1)}</g>`);
    }

    const labelN = isBar ? seriesList[0].nums.length : n;
    const xLabels = Array.isArray(labels) && labels.length === labelN ? labels : Array.from({ length: labelN }, (_, i) => i + 1);

    return html`<div class="aq-chart">
      <svg viewBox=${`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img"
        aria-label=${title || 'chart'}>
        ${title ? html`<text x=${padL} y="20" class="aq-chart-title" font-size="14" font-weight="600" fill="#0f172a">${title}</text>` : null}
        ${grid.map(
          (g, i) => html`<g key=${i}>
            <line x1=${padL} y1=${g.y} x2=${W - padR} y2=${g.y} stroke="#e2e8f0" stroke-width="1"></line>
            <text x=${padL - 6} y=${g.y + 3} text-anchor="end" font-size="10" fill="#94a3b8">${fmtTick(g.val)}</text>
          </g>`
        )}
        <line x1=${padL} y1=${padT + plotH} x2=${W - padR} y2=${padT + plotH} stroke="#cbd5e1" stroke-width="1"></line>
        ${shapeEls}
        ${xLabels.map((lab, i) => {
          if (labelN > 12 && i % Math.ceil(labelN / 12) !== 0 && i !== labelN - 1) return null;
          const cx = isBar ? padL + (i + 0.5) * (plotW / labelN) : xFor(i);
          return html`<text key=${'x' + i} x=${cx} y=${padT + plotH + 16} text-anchor="middle" font-size="10" fill="#94a3b8">${lab}</text>`;
        })}
        ${yAxisLabel ? html`<text x="14" y=${padT + plotH / 2} transform=${`rotate(-90 14 ${padT + plotH / 2})`} text-anchor="middle" font-size="11" fill="#64748b">${yAxisLabel}</text>` : null}
        ${xAxisLabel ? html`<text x=${padL + plotW / 2} y=${H - 6} text-anchor="middle" font-size="11" fill="#64748b">${xAxisLabel}</text>` : null}
      </svg>
      ${showLegend
        ? html`<div class="aq-chart-legend">
            ${seriesList.map((s, i) => html`<span key=${i}><i style=${`background:${s.color}`}></i>${s.name}</span>`)}
          </div>`
        : null}
    </div>`;
  };

  // ---- StatGrid: a denser KPI strip (label + value tiles) -----------------
  const StatGrid = ({ items }) => {
    const list = (Array.isArray(items) ? items : []).filter((x) => x && (x.label != null || x.value != null));
    if (!list.length) return null;
    return html`<div class="aq-statgrid">
      ${list.map((it, i) => {
        const s = toneSuffix(it.tone);
        const cls = s ? `aq-sg-cell aq-sg-cell--${s}` : 'aq-sg-cell';
        return html`<div class=${cls} key=${i}>
          <div class="aq-sg-v">${it.value == null || it.value === '' ? '—' : it.value}${it.unit ? html`<span class="aq-sg-u"> ${it.unit}</span>` : null}</div>
          <div class="aq-sg-l">${it.label || ''}</div>
        </div>`;
      })}
    </div>`;
  };

  // ---- Donut: a single-metric ratio (value / max) as inline SVG -----------
  const Donut = ({ value, max, label }) => {
    const v = Number(value);
    const m = Number(max == null ? 100 : max);
    if (!Number.isFinite(v) || !Number.isFinite(m) || m <= 0) return null;
    const pct = Math.max(0, Math.min(1, v / m));
    const r = 52;
    const c = 2 * Math.PI * r;
    const dash = pct * c;
    const tone = pct >= 0.75 ? '#16a34a' : pct >= 0.4 ? '#d97706' : '#dc2626';
    return html`<div class="aq-donut">
      <svg viewBox="0 0 140 140" width="118" height="118" role="img" aria-label=${label || 'ratio'}>
        <circle cx="70" cy="70" r=${r} fill="none" stroke="#e2e8f0" stroke-width="14"></circle>
        <circle cx="70" cy="70" r=${r} fill="none" stroke=${tone} stroke-width="14" stroke-linecap="round"
          stroke-dasharray=${`${dash.toFixed(1)} ${(c - dash).toFixed(1)}`} transform="rotate(-90 70 70)"></circle>
        <text x="70" y="78" text-anchor="middle" font-size="28" font-weight="700" fill="#0f172a">${Math.round(pct * 100)}%</text>
      </svg>
      ${label ? html`<div class="aq-donut-l">${label}</div>` : null}
    </div>`;
  };

  // ---- ComparisonBar: horizontal bars (label + value/max), pure CSS -------
  const ComparisonBar = ({ rows }) => {
    const list = (Array.isArray(rows) ? rows : []).filter((r) => r && r.label != null);
    if (!list.length) return null;
    const maxAll = Math.max(1, ...list.map((r) => Number(r.max != null ? r.max : r.value) || 0));
    return html`<div class="aq-cmp">
      ${list.map((r, i) => {
        const v = Number(r.value) || 0;
        const m = Number(r.max != null ? r.max : maxAll) || maxAll;
        const pct = Math.max(0, Math.min(100, (v / (m || 1)) * 100));
        const s = toneSuffix(r.tone);
        const fillCls = s ? `aq-cmp-fill aq-cmp-fill--${s}` : 'aq-cmp-fill';
        return html`<div class="aq-cmp-row" key=${i}>
          <div class="aq-cmp-l">${r.label}</div>
          <div class="aq-cmp-track"><div class=${fillCls} style=${`width:${pct.toFixed(1)}%`}></div></div>
          <div class="aq-cmp-v">${r.value == null ? '—' : r.value}${r.unit ? ` ${r.unit}` : ''}</div>
        </div>`;
      })}
    </div>`;
  };

  // ---- Callout: a toned note / alert box (reuses badge tones) -------------
  const Callout = ({ tone, title, children }) => {
    const t = String(tone || '').toLowerCase();
    const cls = ['ok', 'success'].includes(t)
      ? 'aq-callout aq-callout-ok'
      : ['warn', 'pending'].includes(t)
        ? 'aq-callout aq-callout-warn'
        : ['err', 'error', 'fail', 'failed'].includes(t)
          ? 'aq-callout aq-callout-err'
          : 'aq-callout';
    return html`<div class=${cls}>
      ${title ? html`<div class="aq-callout-t">${title}</div>` : null}
      ${children != null ? html`<div class="aq-callout-b">${children}</div>` : null}
    </div>`;
  };

  // ---- location & map (no API key — OpenStreetMap) ------------------------
  // GPS ships as submission meta {latitude,longitude,locationName} and/or a Location field whose value is
  // {latitude,longitude}. Use d.geo('field') to read coordinates. NEVER use Google Static Maps or any map
  // that needs an API key, and never hand-format coordinates with raw HTML entities (TASK-0238).

  // Plain coordinate read-out with a "View on map" link — for when an embedded map is too heavy.
  const Location = ({ lat, lng, name, label }) => {
    const a = Number(lat);
    const b = Number(lng);
    const ok = Number.isFinite(a) && Number.isFinite(b);
    const link = ok ? `https://www.openstreetmap.org/?mlat=${a}&mlon=${b}#map=16/${a}/${b}` : null;
    return html`<div class="aq-loc">
      ${label ? html`<div class="aq-loc-l">${label}</div>` : null}
      ${name ? html`<div class="aq-loc-name">${name}</div>` : null}
      ${ok
        ? html`<div class="aq-loc-co">${coords(a, b)} · <a href=${link} target="_blank" rel="noopener">View on map ↗</a></div>`
        : html`<div class="aq-loc-co aq-loc-empty">No location</div>`}
    </div>`;
  };

  // Static raster map: a grid of OpenStreetMap tiles (plain <img>, no library, no iframe, no API key)
  // centered on the point with a marker. Returns null when coordinates are missing.
  const AqMap = ({ lat, lng, zoom, height, label, name }) => {
    const a = Number(lat);
    const b = Number(lng);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const z = Math.min(19, Math.max(1, Math.round(Number(zoom) || 15)));
    const n = Math.pow(2, z);
    const latRad = (a * Math.PI) / 180;
    const xf = ((b + 180) / 360) * n;
    const yf = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
    const xt = Math.floor(xf);
    const yt = Math.floor(yf);
    const TILE = 256;
    const SPAN = 1; // 3×3 grid
    const cx = (xf - xt) * TILE;
    const cy = (yf - yt) * TILE;
    const tiles = [];
    for (let dy = -SPAN; dy <= SPAN; dy++) {
      for (let dx = -SPAN; dx <= SPAN; dx++) {
        const ty = yt + dy;
        if (ty < 0 || ty >= n) continue;
        const wx = (((xt + dx) % n) + n) % n;
        tiles.push({
          left: (dx + SPAN) * TILE,
          top: (dy + SPAN) * TILE,
          src: `https://tile.openstreetmap.org/${z}/${wx}/${ty}.png`
        });
      }
    }
    // Anchor the grid so the marker point sits at the box center, regardless of responsive box width.
    const anchorX = SPAN * TILE + cx;
    const anchorY = SPAN * TILE + cy;
    const h = Number(height);
    const boxH = Number.isFinite(h) && h > 0 ? h : 280;
    const link = `https://www.openstreetmap.org/?mlat=${a}&mlon=${b}#map=${z}/${a}/${b}`;
    return html`<div class="aq-map">
      <div class="aq-map-view" style=${`height:${boxH}px`}>
        <div class="aq-map-grid" style=${`left:50%;top:50%;transform:translate(${-anchorX}px,${-anchorY}px)`}>
          ${tiles.map(
            (t) =>
              html`<img
                class="aq-map-tile"
                src=${t.src}
                alt=""
                loading="lazy"
                style=${`left:${t.left}px;top:${t.top}px`}
              />`
          )}
        </div>
        <div class="aq-map-pin" aria-hidden="true">📍</div>
      </div>
      <div class="aq-map-cap">
        <span>${name ? `${name} · ` : ''}${coords(a, b)}</span>
        <a href=${link} target="_blank" rel="noopener">View larger map ↗</a>
      </div>
    </div>`;
  };

  // ---- page gadgets (sv10 page variant) ----------------------------------
  // These serve the AI-authored marketing PAGE variant (kind:"page"). They are additive and unused by the
  // submission/list variants, so every existing view renders byte-identically.

  // Copy text to the clipboard, best-effort. Guards the whole call so a missing/blocked Clipboard API (older
  // browser, insecure context) degrades to a no-op rather than throwing out of an onClick handler.
  const copyToClipboard = (text) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(text == null ? '' : text));
      }
    } catch (_e) {}
  };

  // Demo credentials with copy-to-clipboard buttons. Renders only the rows it has.
  const LoginCard = ({ email, password, note }) =>
    html`<div class="aq-login">
      <div class="aq-login-t">Demo login</div>
      ${email
        ? html`<div class="aq-login-row">
            <span class="aq-login-k">Email</span>
            <code class="aq-login-v">${email}</code>
            <button type="button" class="aq-copy" onClick=${() => copyToClipboard(email)}>Copy</button>
          </div>`
        : null}
      ${password
        ? html`<div class="aq-login-row">
            <span class="aq-login-k">Password</span>
            <code class="aq-login-v">${password}</code>
            <button type="button" class="aq-copy" onClick=${() => copyToClipboard(password)}>Copy</button>
          </div>`
        : null}
      ${note ? html`<div class="aq-login-note">${note}</div>` : null}
    </div>`;

  // Titled, bordered iframe embed of a list-viz dashboard. Prefers an explicit `url`; falls back to the
  // relative /v/list/{token} serve. Returns null when neither is present. no-referrer keeps the parent URL
  // out of the embedded request (mirrors the AqMap tile decision to avoid leaking the host).
  const DashboardEmbed = ({ token, url, title, height }) => {
    const src = url || (token ? `/v/list/${token}` : '');
    if (!src) return null;
    const h = Number(height);
    const boxH = Number.isFinite(h) && h > 0 ? h : 720;
    return html`<figure class="aq-embed">
      ${title ? html`<figcaption class="aq-embed-t">${title}</figcaption>` : null}
      <iframe class="aq-embed-frame" src=${src} title=${title || 'Dashboard'} loading="lazy"
        referrerpolicy="no-referrer" style=${`height:${boxH}px`}></iframe>
    </figure>`;
  };

  // A mobile screenshot inside a CSS-only phone bezel. Returns null without a src.
  const PhoneFrame = ({ src, alt }) => {
    if (!src) return null;
    return html`<div class="aq-phone"><div class="aq-phone-screen">
      <img src=${src} alt=${alt || ''} loading="lazy" />
    </div></div>`;
  };

  // Prominent launch button link (new tab). Returns null without an href.
  const CTA = ({ href, label }) => {
    if (!href) return null;
    return html`<a class="aq-cta" href=${href} target="_blank" rel="noopener">${label || 'Launch demo'}</a>`;
  };

  return {
    Hero,
    Card,
    Section,
    KPI,
    Stat,
    Badge,
    FieldList,
    Gallery,
    LabTable,
    RepeatedTable,
    TrendChart,
    StatGrid,
    Donut,
    ComparisonBar,
    Callout,
    Location,
    Map: AqMap,
    LoginCard,
    DashboardEmbed,
    PhoneFrame,
    CTA,
    copy: copyToClipboard
  };
}

// ---- module-local pure helpers -------------------------------------------
// Color cycle for multi-series TrendChart lines (brand blue first, then distinct, color-blind-friendlier hues).
const TREND_PALETTE = ['#2563eb', '#0891b2', '#16a34a', '#d97706', '#9333ea', '#dc2626'];

function scalarText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    return v
      .map((x) => (x && typeof x === 'object' ? (x.fileName || x.url || JSON.stringify(x)) : String(x)))
      .filter((s) => s !== '')
      .join(', ');
  }
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function normalizePoints(points) {
  if (!Array.isArray(points)) return [];
  const out = [];
  for (const p of points) {
    let n;
    if (p && typeof p === 'object' && 'y' in p) n = Number(p.y);
    else n = Number(p);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

function fmtTick(v) {
  const abs = Math.abs(v);
  if (abs >= 1000) return Math.round(v).toString();
  if (abs >= 10) return v.toFixed(0);
  if (abs >= 1) return v.toFixed(1);
  return v.toFixed(2);
}

function formatDate(iso) {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(iso);
    return dt.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return String(iso);
  }
}

function coords(lat, lng) {
  if (lat == null || lng == null) return '';
  const a = Number(lat);
  const b = Number(lng);
  if (Number.isNaN(a) || Number.isNaN(b)) return '';
  return `${a.toFixed(4)}, ${b.toFixed(4)}`;
}

export default createComponents;
